import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Calendar, Users, Star, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GeoShapes({ color = "#1B5F7E", count = 3 }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg className="absolute -top-24 -right-24 opacity-[0.04]" width="500" height="500" viewBox="0 0 200 200">
        <polygon points="100,10 180,180 20,180" fill={color} />
      </svg>
      <svg className="absolute -bottom-16 -left-16 opacity-[0.04]" width="300" height="400" viewBox="0 0 100 140">
        <ellipse cx="50" cy="70" rx="30" ry="65" fill={color} />
      </svg>
      {count > 2 && (
        <svg className="absolute top-1/2 right-0 opacity-[0.03]" width="200" height="400" viewBox="0 0 60 140">
          <path d="M30,0 Q60,70 30,140 Q0,70 30,0Z" fill={color} />
        </svg>
      )}
    </div>
  );
}

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function Home() {
  const [formData, setFormData] = useState({
    nombre: "",
    apellidos: "",
    telefono: "",
    email: "",
    interes: "newsletter",
    mensaje: "",
    comunicaciones_comerciales: false,
  });
  const [formStatus, setFormStatus] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formLoading) return;
    setFormLoading(true);
    setFormStatus(null);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, { ...formData, origen: "home-cta" });
      setFormStatus("success");
    } catch {
      setFormStatus("error");
    } finally {
      setFormLoading(false);
    }
  };

  const testimonials = [
    {
      quote: "Por primera vez en su vida, mi hijo llegó a casa diciendo que había encontrado personas como él. Eso no tiene precio.",
      author: "María J.",
      role: "Madre de participante, 12 años",
      init: "M"
    },
    {
      quote: "Las conversaciones fueron profundas desde el primer momento. No tuve que explicarme. Simplemente... encajé.",
      author: "Roberto A.",
      role: "Participante adulto, Encuentro Gredos",
      init: "R"
    },
    {
      quote: "Dos semanas de campamento transformaron la forma en que mi hija se ve a sí misma. Volvió más segura y con amigos de verdad.",
      author: "Ana M.",
      role: "Madre de participante, 15 años",
      init: "A"
    },
  ];

  const benefits = [
    { title: "Entre iguales", desc: "Conecta con personas que piensan, sienten y procesan el mundo de forma similar.", icon: Users },
    { title: "Estimulación real", desc: "Actividades y conversaciones diseñadas para mentes que necesitan más profundidad.", icon: Star },
    { title: "Convivencia cuidada", desc: "Un entorno seguro donde las diferencias se celebran y los límites se respetan.", icon: Check },
    { title: "Equipo especializado", desc: "Profesionales con formación específica en alta capacidad y desarrollo emocional.", icon: Check },
    { title: "Entorno seguro", desc: "Instalaciones pensadas para la convivencia intensa con total supervisión.", icon: MapPin },
    { title: "Experiencia transformadora", desc: "Una semana puede cambiar la perspectiva de uno mismo para siempre.", icon: ArrowRight },
  ];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex flex-col justify-center pt-20"
        style={{ background: "#F8FAFC" }}
      >
        <GeoShapes color="#1B5F7E" count={3} />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0" style={{ opacity: 0.35 }}>
            <img
              src="/images/campamento/guadarrama-hero.jpg"
              alt="Sierra de Guadarrama"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(248,250,252,0.88) 0%, rgba(248,250,252,0.92) 55%, #F8FAFC 100%)" }} />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="section-label mb-6"
            >
              DAC-PAC · De Alta Capacidad Para Alta Capacidad
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", lineHeight: 1.15 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6"
            >
              Campamento y experiencias<br />
              <em style={{ color: "#1B5F7E" }}>para altas capacidades.</em>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-lg md:text-xl text-slate-600 mb-6 max-w-xl leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Experiencias presenciales para niños, jóvenes y adultos con alta capacidad.
              Campamento de verano, encuentro adultos y extraescolares.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32 }}
              className="text-sm md:text-base text-slate-500 mb-10 max-w-2xl leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Si buscas un campamento para altas capacidades en Espana, aqui puedes explorar el{" "}
              <Link to="/campamento-verano" className="text-[#1B5F7E] font-semibold hover:underline">campamento de verano</Link>, conocer al{" "}
              <Link to="/equipo" className="text-[#1B5F7E] font-semibold hover:underline">equipo liderado por Jana Martinez-Piqueras</Link> y resolver dudas en la{" "}
              <Link to="/faq" className="text-[#1B5F7E] font-semibold hover:underline">pagina de preguntas frecuentes</Link>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4 mb-12"
            >
              <Link to="/campamento-verano" data-testid="hero-cta-campamento" className="btn-outline">
                Campamento Verano
              </Link>
              <Link to="/encuentro-adultos" data-testid="hero-cta-adultos" className="btn-outline">
                Encuentro Adultos
              </Link>
              <Link to="/cursos-especializados" data-testid="hero-cta-cursos" className="btn-outline">
                Extraescolares
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap gap-3 text-xs text-slate-500"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200">
                <span className="w-2 h-2 rounded-full bg-[#2B87BE]" />
                Campamento · próxima edición en preparación
              </span>
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200">
                <span className="w-2 h-2 rounded-full bg-[#2D6A4F]" />
                Adultos · septiembre 2026 · Gredos
              </span>
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200">
                <span className="w-2 h-2 rounded-full bg-[#C9974A]" />
                Extraescolares · Información disponible
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* QUÉ ES DAC-PAC */}
      <section
        data-testid="about-section"
        className="py-28 px-5 lg:px-8"
        style={{ background: "#FFFFFF" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              variants={fadeUp}
            >
              <div className="section-label mb-5">¿Qué es DAC-PAC?</div>
              <h2
                style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", lineHeight: 1.2 }}
                className="text-4xl md:text-5xl font-semibold mb-8"
              >
                Hay personas que siempre<br />han sentido que encajan<br />
                <em style={{ color: "#1B5F7E" }}>en otro lugar.</em>
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>
                <p>
                  Niños que se aburren en clase. Jóvenes que no encuentran su tribu. Adultos que llevan toda la vida explicándose.
                  Personas con una intensidad, una curiosidad y una profundidad que el entorno convencional no sabe dónde poner.
                </p>
                <p>
                  <strong style={{ color: "#0D3A4F" }}>DAC-PAC nace para crear ese lugar.</strong>
                </p>
                <p>
                  Para niños, jóvenes y adultos con alta capacidad. Tres experiencias presenciales, un mismo propósito:
                  que cada persona encuentre su comunidad y se sienta, por fin, comprendida.
                </p>
                <p>
                  Nuestro campamento para altas capacidades, los encuentros y las extraescolares nacen de experiencia real acompanando a familias, ninos, jovenes y adultos con perfiles de alta capacidad.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              variants={fadeUp}
              className="relative"
            >
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 rounded-2xl overflow-hidden" style={{ height: "320px" }}>
                  <img
                    src="/images/home/home-cursos.jpg"
                    alt="Personas conectando en naturaleza"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <div className="rounded-2xl overflow-hidden flex-1">
                    <img
                      src="/images/home/home-cursos-detalle.jpg"
                      alt="Naturaleza Sierra"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    className="rounded-2xl p-5 flex flex-col justify-center"
                    style={{ background: "#0D3A4F", color: "white" }}
                  >
                    <div style={{ fontFamily: "Playfair Display, serif" }} className="text-3xl font-bold">3</div>
                    <div className="text-xs text-[#B8D9E8] mt-1">experiencias<br />presenciales</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXPERIENCIAS */}
      <section
        data-testid="experiences-section"
        className="py-28 px-5 lg:px-8"
        style={{ background: "#F8FAFC" }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <div className="section-label mb-4">Nuestras experiencias</div>
            <h2
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }}
              className="text-4xl md:text-5xl font-semibold"
            >
              Cuatro formas de vivir DAC-PAC
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* CAMPAMENTO KIDS */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              variants={fadeUp}
              data-testid="experience-card-campamento"
            >
              <Link
                to="/campamento-verano"
                className="block rounded-3xl overflow-hidden group experience-card cursor-pointer h-full"
                style={{ background: "#EBF5FB", border: "1px solid #D0E8F5", textDecoration: "none" }}
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src="/images/campamento/grupo-jovenes.jpg"
                    alt="Campamento niños"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <img src="/images/logos/campamento-dacpac.png" alt="" className="h-7 w-7 object-contain" />
                    <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#2B87BE", fontFamily: "Manrope, sans-serif" }}>
                      Campamento DAC-PAC
                    </div>
                  </div>
                  <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-3">
                    Niños y Jóvenes
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Un campamento donde los más intensos, curiosos y diferentes encuentran por fin su tribu.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-xs text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                      <Users size={13} className="text-[#2B87BE]" /> 8 a 18 años
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                      <Calendar size={13} className="text-[#2B87BE]" /> Próxima edición en preparación
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#0D3A4F", fontFamily: "Manrope, sans-serif" }}>
                      Aviso cuando se publique
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: "#2B87BE", fontFamily: "Manrope, sans-serif" }}>
                    Ver información <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* CAMPAMENTO URBANO */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              variants={fadeUp}
              data-testid="experience-card-campamento-urbano"
            >
              <Link
                to="/campamento-verano/campamento-urbano"
                className="block rounded-3xl overflow-hidden group experience-card cursor-pointer h-full"
                style={{ background: "#F5FBFE", border: "1px solid #CDE5F1", textDecoration: "none" }}
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src="/images/campamento/actividades/image00018.jpeg"
                    alt="Actividad del Campamento Urbano DAC-PAC"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <img src="/images/logos/campamento-dacpac.png" alt="" className="h-7 w-7 object-contain" />
                    <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#2B87BE", fontFamily: "Manrope, sans-serif" }}>
                      Campamentos
                    </div>
                  </div>
                  <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-3">
                    Campamento Urbano 2027
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Verano, creatividad, pensamiento lógico, informática, música y tiempos de encuentro en San Sebastián de los Reyes.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-xs text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                      <Calendar size={13} className="text-[#2B87BE]" /> Próxima edición en preparación
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#0D3A4F", fontFamily: "Manrope, sans-serif" }}>
                      Aviso cuando se publique
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: "#2B87BE", fontFamily: "Manrope, sans-serif" }}>
                    Ver información <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* ENCUENTRO ADULTOS */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              variants={fadeUp}
              data-testid="experience-card-adultos"
            >
              <Link
                to="/encuentro-adultos"
                className="block rounded-3xl overflow-hidden group experience-card cursor-pointer h-full"
                style={{ background: "#E8F4EC", border: "1px solid #C5DFD0", textDecoration: "none" }}
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src="/images/encuentro/adultos-encuentro.jpg"
                    alt="Encuentro adultos"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <img src="/images/logos/encuentro-dacpac.png" alt="" className="h-7 w-7 object-contain" />
                    <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#2D6A4F", fontFamily: "Manrope, sans-serif" }}>
                      Encuentro DAC-PAC
                    </div>
                  </div>
                  <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-3">
                    DAC-PAC Adultos
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Próxima edición en septiembre de 2026: naturaleza, conversaciones profundas y comunidad entre iguales.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-xs text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                      <Calendar size={13} className="text-[#2D6A4F]" /> Septiembre 2026 · La Lobera de Gredos
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#2D6A4F", fontFamily: "Manrope, sans-serif" }}>
                      20 plazas disponibles
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: "#2D6A4F", fontFamily: "Manrope, sans-serif" }}>
                    Solicitar información <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* CURSOS */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              variants={fadeUp}
              data-testid="experience-card-cursos"
            >
              <Link
                to="/cursos-especializados"
                className="block rounded-3xl overflow-hidden group experience-card cursor-pointer h-full"
                style={{ background: "#FBF3E3", border: "1px solid #EEDDB8", textDecoration: "none" }}
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src="/images/cursos/hero-cursos.jpg"
                    alt="Extraescolares DAC-PAC"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <img src="/images/logos/cursos-dacpac.png" alt="" className="h-6 w-6 object-contain" />
                    <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#C9974A", fontFamily: "Manrope, sans-serif" }}>
                      Extraescolares
                    </div>
                  </div>
                  <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-3">
                    DAC-PAC
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Actividades semanales con reto real, grupos reducidos y metodología diseñada para alta curiosidad.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-xs text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                      <Star size={13} className="text-[#C9974A]" /> Plazas reducidas
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#C9974A", fontFamily: "Manrope, sans-serif" }}>
                      Curso 2026/2027 · Información disponible
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all" style={{ color: "#C9974A", fontFamily: "Manrope, sans-serif" }}>
                    Ver extraescolares <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POR QUÉ DAC-PAC */}
      <section
        data-testid="benefits-section"
        className="py-28 px-5 lg:px-8"
        style={{ background: "#FFFFFF" }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <div className="section-label mb-4">¿Por qué DAC-PAC?</div>
            <h2
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }}
              className="text-4xl md:text-5xl font-semibold"
            >
              Lo que hace diferente<br />a DAC-PAC
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                variants={fadeUp}
                data-testid={`benefit-card-${i}`}
                className="p-7 rounded-2xl border border-slate-100 hover:border-[#B8D9E8] hover:shadow-lg transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ background: "#EBF5FB" }}>
                  <b.icon size={18} style={{ color: "#1B5F7E" }} />
                </div>
                <h4 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-lg font-semibold mb-3">
                  {b.title}
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {b.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO INSCRIBIRSE */}
      <section
        data-testid="process-section"
        className="py-24 px-5 lg:px-8"
        style={{ background: "#F8FAFC" }}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <div className="section-label mb-4">Reserva tu plaza</div>
            <h2
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }}
              className="text-4xl md:text-5xl font-semibold"
            >
              ¿Cómo me inscribo?
            </h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>
              El proceso es sencillo y siempre hay una persona real al otro lado.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                num: "01",
                title: "Solicita preinscripción",
                desc: "Rellena el formulario de preinscripción o escríbenos directamente. Solo tarda un minuto.",
                cta: "Ir a preinscripción",
                href: "/preinscripcion",
                color: "#1B5F7E"
              },
              {
                num: "02",
                title: "Te contactamos en 48h",
                desc: "Un miembro del equipo se pone en contacto contigo personalmente para resolver dudas y confirmar disponibilidad.",
                cta: null,
                color: "#1B5F7E"
              },
              {
                num: "03",
                title: "Confirmas la plaza",
                desc: "Con el pago de una señal queda reservada tu plaza. El resto se abona antes del inicio.",
                cta: null,
                color: "#1B5F7E"
              },
              {
                num: "04",
                title: "Vives DAC-PAC",
                desc: "Te enviamos toda la información práctica: horarios, qué traer, contacto de monitores.",
                cta: null,
                color: "#1B5F7E"
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                variants={fadeUp}
                data-testid={`process-step-${i + 1}`}
                className="relative p-7 rounded-2xl bg-white border border-slate-100 flex flex-col"
                style={{ boxShadow: "0 2px 16px rgba(27,95,126,0.06)" }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 text-white text-sm font-bold flex-shrink-0"
                  style={{ background: "#1B5F7E", fontFamily: "Manrope, sans-serif" }}
                >
                  {step.num}
                </div>
                <h4 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-lg font-semibold mb-3">
                  {step.title}
                </h4>
                <p className="text-slate-500 text-sm leading-relaxed flex-1" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {step.desc}
                </p>
                {step.cta && (
                  <Link
                    to={step.href}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold mt-4"
                    style={{ color: "#1B5F7E", fontFamily: "Manrope, sans-serif" }}
                  >
                    {step.cta} <ArrowRight size={12} />
                  </Link>
                )}
                {/* Connector arrow */}
                {i < 3 && (
                  <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 text-slate-300">
                    <ArrowRight size={16} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section
        data-testid="testimonials-section"
        className="py-28 px-5 lg:px-8"
        style={{ background: "#FFFFFF" }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <div className="section-label mb-4">Testimonios</div>
            <h2
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }}
              className="text-4xl md:text-5xl font-semibold"
            >
              Lo que dicen las familias
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                variants={fadeUp}
                data-testid={`testimonial-card-${i}`}
                className="testimonial-card"
              >
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={14} className="fill-current" style={{ color: "#E9C46A" }} />
                  ))}
                </div>
                <p className="text-slate-600 leading-relaxed mb-6 text-sm italic" style={{ fontFamily: "Playfair Display, serif" }}>
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ background: "#1B5F7E" }}
                  >
                    {t.init}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Manrope, sans-serif" }}>{t.author}</div>
                    <div className="text-xs text-slate-500" style={{ fontFamily: "Manrope, sans-serif" }}>{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section
        data-testid="cta-section"
        className="py-28 px-5 lg:px-8"
        style={{ background: "#F0F9FF" }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
          >
            <div className="section-label mb-5">Avísame</div>
            <h2
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }}
              className="text-4xl md:text-5xl font-semibold mb-4"
            >
              ¿Quieres que te avisemos<br />cuando se abran plazas?
            </h2>
            <p className="text-slate-500 mb-10" style={{ fontFamily: "Manrope, sans-serif" }}>
              Déjanos tu contacto y serás el primero en saber cuándo hay disponibilidad.
            </p>

            {formStatus === "success" ? (
              <div
                data-testid="cta-success-message"
                className="rounded-2xl p-8 text-center"
                style={{ background: "#E8F4EC", border: "1px solid #C5DFD0" }}
              >
                <Check size={32} className="mx-auto mb-4" style={{ color: "#2D6A4F" }} />
                <p style={{ fontFamily: "Manrope, sans-serif", color: "#2D6A4F", fontWeight: 600 }}>
                  ¡Anotado! Te avisaremos cuando se abran plazas.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="cta-form" className="space-y-4">
                <input
                  type="text"
                  placeholder="Tu nombre"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  data-testid="cta-nombre-input"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                />
                <input
                  type="text"
                  placeholder="Tus apellidos"
                  required
                  value={formData.apellidos}
                  onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                  data-testid="cta-apellidos-input"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                />
                <input
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  data-testid="cta-telefono-input"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                />
                <input
                  type="email"
                  placeholder="Tu email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="cta-email-input"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                />
                <textarea
                  placeholder="Algo que nos quieras comentar"
                  value={formData.mensaje}
                  onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                  data-testid="cta-mensaje-textarea"
                  rows={3}
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm resize-none"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                />
                <select
                  hidden
                  value={formData.interes}
                  onChange={(e) => setFormData({ ...formData, interes: e.target.value })}
                  data-testid="cta-interes-select"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#1B5F7E] focus:ring-2 focus:ring-[#1B5F7E]/10 text-sm"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  <option value="newsletter">Newsletter</option>
                  <option value="campamento-urbano-2027">Campamento Urbano 2027</option>
                  <option value="campamento">Campamentos de verano (8–18 años)</option>
                  <option value="encuentro-adultos">Encuentro Adultos</option>
                  <option value="cursos">Extraescolares</option>
                </select>
                <label className="flex items-start gap-3 cursor-pointer text-left" data-testid="cta-comercial-checkbox">
                  <input
                    type="checkbox"
                    required
                    checked={formData.comunicaciones_comerciales}
                    onChange={(e) => setFormData({ ...formData, comunicaciones_comerciales: e.target.checked })}
                    className="mt-1 w-4 h-4 rounded border-slate-300 focus:ring-2"
                    style={{ accentColor: "#1B5F7E" }}
                  />
                  <span className="text-sm text-slate-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Quiero recibir comunicaciones comerciales sobre DAC-PAC
                  </span>
                </label>
                {formStatus === "error" && (
                  <p className="text-red-500 text-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Ha ocurrido un error. Por favor, inténtalo de nuevo.
                  </p>
                )}
                <button type="submit" disabled={formLoading} data-testid="cta-submit-btn" className="btn-primary w-full justify-center py-4 disabled:opacity-60">
                  {formLoading ? "Enviando..." : "Avísame cuando se abran plazas"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </main>
  );
}
